<script setup lang="ts">
  import WhiteLogo from '@/components/Logo.vue'
  import type { PortalCustomizationColors } from '@/types/nest/portal-customization'
  import { isLightColor, resolvePortalColors } from '@/utils/portal-theme'
  import { Logo } from '@logistiek-zonder-papier/lzp-library'
  import { computed } from 'vue'

  const { colors, logoUrl } = defineProps<{
    colors: PortalCustomizationColors
    logoUrl: string | null
  }>()

  const resolved = computed(() => resolvePortalColors(colors))

  const previewStyle = computed(() => ({
    '--preview-primary': resolved.value.primaryBrandColor,
    '--preview-primary-text': resolved.value.primaryForegroundColor,
    '--preview-sidebar': resolved.value.secondaryBrandColor,
    '--preview-sidebar-text': resolved.value.secondaryForegroundColor,
  }))

  const useWhiteLogo = computed<boolean>(() =>
    isLightColor(resolved.value.secondaryForegroundColor)
  )

  const menuItems = [
    { label: 'Zendingen', active: false },
    { label: 'Organisatie', active: true },
    { label: 'Gebruikers', active: false },
  ]
</script>

<template>
  <div class="preview" :style="previewStyle" aria-hidden="true">
    <div class="window-bar">
      <span class="dot" />
      <span class="dot" />
      <span class="dot" />
    </div>

    <div class="window">
      <aside class="sidebar">
        <div class="logo">
          <img v-if="logoUrl" class="custom-logo" :src="logoUrl" alt="" />
          <WhiteLogo v-else-if="useWhiteLogo" class="default-logo" />
          <Logo v-else class="default-logo" />
        </div>

        <ul class="menu">
          <li
            v-for="item in menuItems"
            :key="item.label"
            class="menu-item"
            :class="{ active: item.active }"
          >
            <span class="menu-icon" />
            {{ item.label }}
          </li>
        </ul>

        <div class="account">
          <span class="avatar" />
          <span class="line short" />
        </div>
      </aside>

      <main class="content">
        <div class="title">Organisatie</div>
        <div class="tabs">
          <span class="tab active" />
          <span class="tab" />
          <span class="tab" />
        </div>

        <div class="field">
          <span class="line label" />
          <span class="input" />
        </div>
        <div class="field">
          <span class="line label" />
          <span class="input" />
        </div>

        <div class="actions">
          <span class="link">Annuleren</span>
          <span class="button">Opslaan</span>
        </div>
      </main>
    </div>
  </div>
</template>

<style scoped lang="css">
  .preview {
    border: 1px solid var(--colours-grey-3);
    border-radius: 0.875rem;
    overflow: hidden;
    background: var(--colours-grey-1);
    box-shadow:
      0 1px 2px var(--colours-shadow-1),
      0 0.5rem 1.5rem var(--colours-shadow-2);
    font-size: 0.6875rem;
    line-height: 1.3;
    user-select: none;
  }

  .window-bar {
    display: flex;
    gap: 0.3125rem;
    padding: var(--spacing-xs) 0.625rem;
    border-bottom: 1px solid var(--colours-grey-3);
    background: var(--colours-grey-1);
  }

  .dot {
    width: 0.4375rem;
    height: 0.4375rem;
    border-radius: 50%;
    background: var(--colours-grey-3);
  }

  .window {
    display: grid;
    grid-template-columns: 38% 1fr;
    height: 14.5rem;
  }

  .sidebar {
    display: flex;
    flex-direction: column;
    border-right: 1px solid color-mix(in srgb, var(--preview-sidebar-text) 15%, transparent);
    background: var(--preview-sidebar);
    color: var(--preview-sidebar-text);
    transition:
      background-color 150ms ease-in-out,
      color 150ms ease-in-out;
  }

  .logo {
    display: grid;
    place-items: center;
    height: 2.75rem;
    padding: 0.375rem 0.625rem;
    border-bottom: 1px solid color-mix(in srgb, var(--preview-sidebar-text) 15%, transparent);
  }

  .custom-logo {
    max-width: 100%;
    max-height: 1.875rem;
    object-fit: contain;
  }

  .default-logo {
    height: 1.625rem;
    width: auto;
  }

  .menu {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    margin: 0;
    padding: var(--spacing-xs) 0.375rem;
    list-style: none;
  }

  .menu-item {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.3125rem var(--spacing-xs);
    border-radius: 999px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
  }

  .menu-item.active {
    background: var(--preview-primary);
    color: var(--preview-primary-text);
  }

  .menu-icon {
    flex-shrink: 0;
    width: 0.5rem;
    height: 0.5rem;
    border: 1.5px solid currentColor;
    border-radius: 0.125rem;
    opacity: 0.8;
  }

  .account {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    margin-top: auto;
    padding: var(--spacing-xs) 0.75rem 0.625rem;
    border-top: 1px solid color-mix(in srgb, var(--preview-sidebar-text) 15%, transparent);
  }

  .avatar {
    width: 0.75rem;
    height: 0.75rem;
    border: 1.5px solid currentColor;
    border-radius: 50%;
    opacity: 0.8;
  }

  .line {
    display: block;
    height: 0.375rem;
    border-radius: 0.1875rem;
    background: currentColor;
    opacity: 0.25;
  }

  .line.short {
    width: 3rem;
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 0.5625rem;
    min-width: 0;
    padding: 0.75rem 0.875rem;
    background: var(--colours-grey-2);
    color: var(--colours-grey-5);
  }

  .title {
    font-size: 0.9375rem;
    font-weight: 700;
  }

  .tabs {
    display: flex;
    gap: var(--spacing-xs);
    padding-bottom: 0.375rem;
    border-bottom: 1px solid var(--colours-grey-3);
  }

  .tab {
    width: 1.625rem;
    height: 0.3125rem;
    border-radius: 0.1875rem;
    background: var(--colours-grey-3);
  }

  .tab.active {
    background: var(--colours-grey-5);
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .line.label {
    width: 40%;
  }

  .input {
    height: 1rem;
    border: 1px solid var(--colours-grey-3);
    border-radius: 0.25rem;
    background: var(--colours-grey-1);
  }

  .actions {
    display: flex;
    justify-content: end;
    align-items: center;
    gap: var(--spacing-xs);
    margin-top: auto;
  }

  .link {
    color: var(--preview-primary);
    font-weight: 600;
  }

  .button {
    padding: 0.3125rem 0.75rem;
    border-radius: 999px;
    background: var(--preview-primary);
    color: var(--preview-primary-text);
    font-weight: 600;
    transition:
      background-color 150ms ease-in-out,
      color 150ms ease-in-out;
  }

  @media (prefers-reduced-motion: reduce) {
    .sidebar,
    .button {
      transition: none;
    }
  }
</style>
